import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Mail, 
  Trash2, 
  Check, 
  LogOut, 
  RefreshCw, 
  MapPin, 
  Clock, 
  User,
  Globe,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Settings,
  Shield,
  ShieldOff,
  Sun,
  Moon,
  Palette,
  Key,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BookingManagement from '@/components/BookingManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('messages'); // 'messages', 'bookings', 'settings'
  const [settings, setSettings] = useState({ dev_tools_disabled: false, theme: 'dark' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Password change states
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const getAuthHeader = () => {
    const credentials = sessionStorage.getItem('adminAuth');
    if (!credentials) {
      navigate('/ma');
      return null;
    }
    return { 'Authorization': `Basic ${credentials}` };
  };

  const fetchMessages = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/messages`, { headers });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else if (response.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/ma');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchStats = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/stats`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    await Promise.all([fetchMessages(), fetchStats(), fetchSettings()]);
    setIsLoading(false);
  };

  const fetchSettings = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSettingsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    // Validation
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters!' });
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;

    setPasswordLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully! Please login again.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Logout after 2 seconds
        setTimeout(() => {
          sessionStorage.removeItem('adminAuth');
          navigate('/ma');
        }, 2000);
      } else {
        setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAsRead = async (messageId) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/messages/${messageId}/read`, {
        method: 'PUT',
        headers
      });
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    const headers = getAuthHeader();
    if (!headers) return;

    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers
      });
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/ma');
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background admin-dashboard">
      {/* Header */}
      <header className="glass-strong border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Manage your portfolio</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="container mx-auto px-4 pb-0">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'messages'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              Messages
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              data-testid="bookings-tab"
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'bookings'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Bookings
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === 'settings'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : activeTab === 'bookings' ? (
          /* Bookings Tab Content */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="bookings-management"
          >
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Booking Management
            </h2>
            <BookingManagement getAuthHeader={getAuthHeader} />
          </motion.div>
        ) : activeTab === 'settings' ? (
          /* Settings Tab Content */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Site Settings
            </h2>

            <div className="space-y-6 max-w-2xl">
              {/* Theme Selection */}
              <div className="glass-strong rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center">
                    <Palette className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Theme Selection</h3>
                    <p className="text-sm text-muted-foreground">Choose how your website appears to visitors</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4">
                  {/* Dark Theme */}
                  <button
                    onClick={() => updateSettings({ theme: 'dark' })}
                    disabled={settingsLoading}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      settings.theme === 'dark' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Moon className={`w-8 h-8 mx-auto mb-2 ${settings.theme === 'dark' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`text-sm font-medium ${settings.theme === 'dark' ? 'text-foreground' : 'text-muted-foreground'}`}>Dark</p>
                    <p className="text-xs text-muted-foreground mt-1">Night mode only</p>
                  </button>

                  {/* Light Theme */}
                  <button
                    onClick={() => updateSettings({ theme: 'light' })}
                    disabled={settingsLoading}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      settings.theme === 'light' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Sun className={`w-8 h-8 mx-auto mb-2 ${settings.theme === 'light' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`text-sm font-medium ${settings.theme === 'light' ? 'text-foreground' : 'text-muted-foreground'}`}>Light</p>
                    <p className="text-xs text-muted-foreground mt-1">Day mode only</p>
                  </button>

                  {/* Optional (Toggle) */}
                  <button
                    onClick={() => updateSettings({ theme: 'optional' })}
                    disabled={settingsLoading}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      settings.theme === 'optional' 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      <Sun className={`w-5 h-5 ${settings.theme === 'optional' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="text-muted-foreground">/</span>
                      <Moon className={`w-5 h-5 ${settings.theme === 'optional' ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <p className={`text-sm font-medium ${settings.theme === 'optional' ? 'text-foreground' : 'text-muted-foreground'}`}>Optional</p>
                    <p className="text-xs text-muted-foreground mt-1">User can toggle</p>
                  </button>
                </div>

                {settings.theme === 'optional' && (
                  <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-sm text-foreground">
                      <strong>Note:</strong> A toggle icon will appear in the navigation for visitors to switch themes.
                    </p>
                  </div>
                )}
              </div>

              {/* Developer Tools Toggle */}
              <div className="glass-strong rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      settings.dev_tools_disabled 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                        : 'bg-muted'
                    }`}>
                      {settings.dev_tools_disabled ? (
                        <Shield className="w-6 h-6 text-white" />
                      ) : (
                        <ShieldOff className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Developer Tools Protection</h3>
                      <p className="text-sm text-muted-foreground">
                        {settings.dev_tools_disabled 
                          ? 'Protection enabled - Right-click, Ctrl+U, F12 are disabled'
                          : 'Protection disabled - Developer tools are accessible'}
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => updateSettings({ dev_tools_disabled: !settings.dev_tools_disabled })}
                    disabled={settingsLoading}
                    variant={settings.dev_tools_disabled ? "destructive" : "default"}
                    className={!settings.dev_tools_disabled ? "bg-gradient-primary" : ""}
                  >
                    {settingsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : settings.dev_tools_disabled ? (
                      'Turn OFF'
                    ) : (
                      'Turn ON'
                    )}
                  </Button>
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">When enabled:</strong>
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                    <li>Right-click context menu is disabled</li>
                    <li>Keyboard shortcuts (Ctrl+U, Ctrl+Shift+I, F12) are blocked</li>
                    <li>View source and inspect element are prevented</li>
                  </ul>
                </div>
              </div>

              {settings.updated_at && (
                <p className="text-xs text-muted-foreground">
                  Last updated: {new Date(settings.updated_at).toLocaleString()}
                </p>
              )}

              {/* Password Change Section */}
              <div className="glass-strong rounded-xl p-6">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowPasswordSection(!showPasswordSection)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                      <Key className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Change Password</h3>
                      <p className="text-sm text-muted-foreground">Update your admin password</p>
                    </div>
                  </div>
                  {showPasswordSection ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>

                <AnimatePresence>
                  {showPasswordSection && (
                    <motion.form
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      onSubmit={handlePasswordChange}
                      className="mt-6 space-y-4 overflow-hidden"
                    >
                      {/* Current Password */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <Input
                            type={showCurrentPwd ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                            required
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* New Password */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <Input
                            type={showNewPwd ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min 6 characters)"
                            required
                            minLength={6}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPwd(!showNewPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">
                          Confirm New Password
                        </label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          required
                        />
                      </div>

                      {/* Status Message */}
                      {passwordMessage.text && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`p-3 rounded-lg flex items-center gap-2 ${
                            passwordMessage.type === 'success' 
                              ? 'bg-green-500/10 border border-green-500/20 text-green-500' 
                              : 'bg-red-500/10 border border-red-500/20 text-red-500'
                          }`}
                        >
                          {passwordMessage.type === 'success' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          <span className="text-sm">{passwordMessage.text}</span>
                        </motion.div>
                      )}

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        className="w-full bg-gradient-primary"
                      >
                        {passwordLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Changing Password...
                          </>
                        ) : (
                          <>
                            <Key className="w-4 h-4 mr-2" />
                            Change Password
                          </>
                        )}
                      </Button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
              >
                <div className="glass-strong rounded-xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.total_messages}</p>
                      <p className="text-sm text-muted-foreground">Total Messages</p>
                    </div>
                  </div>
                </div>

                <div className="glass-strong rounded-xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stats.unread_messages}</p>
                      <p className="text-sm text-muted-foreground">Unread</p>
                    </div>
                  </div>
                </div>

                <div className="glass-strong rounded-xl p-5 sm:col-span-2">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">Top Countries</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stats.country_stats?.map((country, index) => (
                      <span key={index} className="px-3 py-1 rounded-full bg-muted text-sm text-muted-foreground">
                        {country._id}: {country.count}
                      </span>
                    ))}
                    {(!stats.country_stats || stats.country_stats.length === 0) && (
                      <span className="text-sm text-muted-foreground">No data yet</span>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Messages List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Messages
              </h2>

              {messages.length === 0 ? (
                <div className="glass-strong rounded-xl p-12 text-center">
                  <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No messages yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence>
                    {messages.map((msg, index) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ delay: index * 0.05 }}
                        className={`glass-strong rounded-xl overflow-hidden ${!msg.is_read ? 'border-l-4 border-primary' : ''}`}
                      >
                        {/* Message Header */}
                        <div
                          className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => setExpandedMessage(expandedMessage === msg.id ? null : msg.id)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="font-semibold text-foreground">{msg.name}</span>
                                {!msg.is_read && (
                                  <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">New</span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {msg.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {msg.city}, {msg.country}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(msg.timestamp)}
                                </span>
                              </div>
                              <p className="text-muted-foreground mt-2 line-clamp-1">{msg.message}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {expandedMessage === msg.id ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        <AnimatePresence>
                          {expandedMessage === msg.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="border-t border-border/50"
                            >
                              <div className="p-4 bg-muted/20">
                                <p className="text-sm text-muted-foreground mb-2">Full Message:</p>
                                <p className="text-foreground whitespace-pre-wrap">{msg.message}</p>
                                
                                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border/50">
                                  <span className="text-xs text-muted-foreground">IP: {msg.ip_address}</span>
                                </div>

                                <div className="flex items-center gap-2 mt-4">
                                  {!msg.is_read && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => markAsRead(msg.id)}
                                    >
                                      <Check className="w-4 h-4" />
                                      Mark as Read
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    asChild
                                  >
                                    <a href={`mailto:${msg.email}`}>
                                      <Mail className="w-4 h-4" />
                                      Reply
                                    </a>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                    onClick={() => deleteMessage(msg.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
